
import React, { useMemo } from "react";
import { Card } from "react-bootstrap";
import * as echarts from 'echarts/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { EChartsOption } from 'echarts';

import useTranslation from "@/Hooks/UseTranslation";
import { useAuth } from "@/Hooks/UseAuth";
import { useProject } from "@/Hooks/UseProject";
import moment from "moment";

interface Props {
  progress: number
  deviation: number
  estimatedEndDate: string
  performance: string
  projectStatus: 'overdue' | 'on-time' | 'advanced' | 'pending-start' | 'with-uncompleted-closure' | 'closed' | null
}

const EstimatedDeviation = ({ progress, deviation, estimatedEndDate, performance, projectStatus }: Props) => {

  const { t } = useTranslation()

  const { userData } = useAuth()

  const { project } = useProject()

  const [ statusClass, progressColor ] = useMemo(() => {

    if (projectStatus === 'overdue')
      return ['status-atrasada', '#ff0000']

    if (projectStatus === 'on-time')
      return ['status-puntual', '#1960ca']

    if (projectStatus === 'advanced')
      return ['status-adelantada', '#34a854']

    if (projectStatus === 'pending-start' || projectStatus === 'with-uncompleted-closure')
      return ['status-outoftime', '#646363']

    return ['status-puntual', '#1960ca']
  }, [projectStatus])

  const option = useMemo<EChartsOption>(() => ({
    series: [
      {
        type: 'gauge',
        min: 0,
        max: 100,
        height: 300,
        width: '100%',
        center: ['50%', '50%'],
        progress: {
          show: true,
          width: 40,
          itemStyle: {
            color: progressColor,
            borderColor: '#fff',
            borderWidth: 1,
          }
        },
        axisLine: {
          lineStyle: {
            width: 40,
            color: [[1, '#E0E0E0']], // fondo
          }
        },
        pointer: {
          itemStyle: {
            color: '#817e7e'
          }
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        title: { show: false },
        detail: {
          valueAnimation: true,
          formatter: '{value}%',
          color: progressColor
        },
        data: [{ value: progress }],
      }
    ]
  }), [progress])

  const getDelayedHeaderText = () => {

    const percent = `${Math.abs(deviation)}`

    const text = t('PROJECT_DASHBOARD:SummaryDelayedWeAreXDaysBehind')

    return text.replace('{percent}', performance)
  }

  const getAdvancedHeaderText = () => {

    const percent = `${Math.abs(deviation)}`

    const text = t('PROJECT_DASHBOARD:SummaryWeAreXDaysAheadOfSchedule')

    return text.replace('{percent}', performance)
  }

  const getUncompletedClosureHeaderText = () => {

    const text = t('PROJECT_DASHBOARD:SummaryUnCompletedClosureOne')

    return text.replace('{percent}', performance)
  }

  const getPendingStartBodyText = () => {

    const text = t('PROJECT_DASHBOARD:SummaryPendingStartTwo')

    return text.replace('{date}', moment(project.startsAt).format('DD/MM/YYYY'))
  }

  return (
    <Card className={`summary-card ${statusClass}`}>
      <Card.Body>
        <h4 className='card-title'>
          {projectStatus === 'overdue' && t('PROJECT_DASHBOARD:SummaryProjectOverdueTitle')}
          {projectStatus === 'on-time' && t('PROJECT_DASHBOARD:SummaryProjectOnTimeTitle')}
          {projectStatus === 'advanced' && t('PROJECT_DASHBOARD:SummaryProjectAdvancedTitle')}
          {projectStatus === 'pending-start' && t('PROJECT_DASHBOARD:SummaryProjectPendingStartTitle')}
          {projectStatus === 'with-uncompleted-closure' && t('PROJECT_DASHBOARD:SummaryProjectWithUncompletedClosureTitle')}
        </h4>
        <div className="summary-body">
          <div className="summary-chart" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ReactEChartsCore
              echarts={echarts}
              option={option}
              style={{ height: '332px', width: '332px' }}
            />
          </div>
          <div className="summary-texts">
            <div className="welcome">
              <h4>{t('PROJECT_DASHBOARD:SummaryHiText')} {userData?.name},</h4>
            </div>
            <div className="summary">
              {projectStatus === 'overdue' && (
                <p>
                  <span className="bolded">
                    {getDelayedHeaderText()}
                  </span>  <br />
                  {t('PROJECT_DASHBOARD:SummaryIfWeContinueAtThisPaceWeWillFinishOn')} {estimatedEndDate}. <br />
                  {t('PROJECT_DASHBOARD:SummaryItsTimeToAdjustThePlanAndGetBackIntoTheSwingOfThings')}
                </p>
              )}
              {projectStatus === 'advanced' && (
                <p>
                  <span className="bolded">
                    {getAdvancedHeaderText()}
                  </span> <br />
                  {t('PROJECT_DASHBOARD:SummaryIfWeContinueAtThisPaceWeWillFinishOn')} {estimatedEndDate}. <br />
                  {t('PROJECT_DASHBOARD:SummaryLetsKeepItUp')}
                </p>
              )}
              {projectStatus === 'on-time' && (
                <p>
                  <span className="bolded">{t('PROJECT_DASHBOARD:SummaryWeAreGoingAccordingToPlan')}</span> <br />
                  {t('PROJECT_DASHBOARD:SummaryIfWeContinueAtThisPaceWeWillFinishOn')} {estimatedEndDate}. <br />
                  {t('PROJECT_DASHBOARD:SummaryLetsKeepThePace')}
                </p>
              )}
              {projectStatus === 'pending-start' && (
                <p>
                  <span className="bolded">
                    {t('PROJECT_DASHBOARD:SummaryPendingStartOne')}
                  </span> <br />
                  {getPendingStartBodyText()}
                </p>
              )}
              {projectStatus === 'with-uncompleted-closure' && (
                <p>
                  <span className="bolded">
                    {getUncompletedClosureHeaderText()}
                  </span> <br />
                  {t('PROJECT_DASHBOARD:SummaryUnCompletedClosureTwo')}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export default EstimatedDeviation;
